/**
 * FormattedExcelExportService - Advanced Excel export with professional formatting
 * 
 * This service provides comprehensive Excel export functionality using ExcelJS
 * for full formatting support including colors, rich text, formulas, and styling.
 * 
 * Features:
 * - Multi-color transaction text within cells
 * - Professional table formatting with borders and alignment
 * - White backgrounds with colored text for clarity
 * - GARCH column data matching screen table exactly
 * - Unified export for both 3-Topf and 1-Topf strategies
 * 
 * @author Portfolio-3Topf Team
 * @version 3.0.0
 */

import * as ExcelJS from 'exceljs';

/**
 * Helper functions to get readable labels for simulation options
 */
const getInflationOptionLabel = (value: string, steuerParameter?: { inflationsrate?: number }): string => {
  switch (value) {
    case 'keine-inflation': return 'Keine Inflation (0%)';
    case 'parameter-inflation': return `Inflation mit Prozentwert aus Eingabeparameter (${steuerParameter?.inflationsrate || 2.0}%)`;
    case 'historische-inflation': return 'Inflation als Zufallswert aus historische Inflation';
    case 'statistisch-inflation': return 'Inflation als Zufallswert basierend auf Mittelwert und Standardabweichung';
    default: return 'Keine Inflation (0%)';
  }
};

const getInvestmentReturnOptionLabel = (value: string, steuerParameter?: { renditeInvestitionstopf?: number }): string => {
  switch (value) {
    case 'keine-rendite': return 'Keine Rendite (0%)';
    case 'konstante-rendite': return `Konstante Rendite aus Parameterwert (${steuerParameter?.renditeInvestitionstopf || 7.0}%)`;
    case 'historische-rendite': return 'Rendite als Zufallswert aus MSCI World historischen Renditen';
    case 'statistisch-rendite': return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Renditen des MSCI World';
    case 'historische-garch': return '📈 Realistische Krisen-Modellierung: Historische Marktdaten + intelligente Volatilitäts-Cluster (inkl. 2008, 2000, COVID)';
    case 'statistisch-garch': return '🎯 Theoretische Modellierung: Statistische Verteilung + zeitvariable Volatilität (glatte Übergänge, kontrollierbar)';
    default: return `Konstante Rendite aus Parameterwert (${steuerParameter?.renditeInvestitionstopf || 7.0}%)`;
  }
};

const getTagesgeldOptionLabel = (value: string, steuerParameter?: { zinssatzVerbrauchstopf?: number }): string => {
  switch (value) {
    case 'keine-rendite': return 'Keine Rendite (0%)';
    case 'parameter-rendite': return `Rendite aus Parameterwert (${steuerParameter?.zinssatzVerbrauchstopf || 1.5}%)`;
    case 'historisch-tagesgeld': return 'Rendite als Zufallswert aus historischen Tagesgeld-Zinssätzen';
    case 'statistisch-tagesgeld': return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Tagesgeld-Zinssätze';
    default: return 'Keine Rendite (0%)';
  }
};

const getZinstopfOptionLabel = (value: string, steuerParameter?: { zinssatzZinstopf?: number }): string => {
  switch (value) {
    case 'keine-rendite': return 'Keine Rendite (0%)';
    case 'parameter-rendite': return `Rendite aus Parameterwert (${steuerParameter?.zinssatzZinstopf || 3.0}%)`;
    case 'historisch-zinstopf': return 'Rendite als Zufallswert aus historischen Zinstopf-Performances';
    case 'statistisch-zinstopf': return 'Rendite als Zufallswert basierend auf Mittelwert und Standardabweichung der historischen Zinstopf-Performances';
    default: return 'Keine Rendite (0%)';
  }
};

const getCrisisDefinitionLabel = (value: string): string => {
  switch (value) {
    case 'negative-return': return 'Krise bei negativer Jahresrendite';
    case 'kapitalindex-drawdown': return 'Krise bei Kapitalindex-Rückgang - Ende erst bei Erholung';
    case 'negative-rendite': return 'Negative Rendite'; // Legacy support
    case 'minus-10-prozent': return 'Rendite unter -10%'; // Legacy support
    default: return 'Krise bei negativer Jahresrendite';
  }
};

/**
 * Helper function to format market situation with dual timing (01.01 and 31.12)
 * Returns either a string or rich text object for Excel formatting
 */
const formatMarktSituationWithDualTiming = (
  marktSituation: string,
  startOfYearStatus: string
): string | { richText: any[] } => {
  // Create dual timing format: "01.01: Status\n31.12: Status"
  const startOfYearColor = startOfYearStatus === 'Krise' ? 'FFDC2626' : '16B364'; // Red or Green
  const endOfYearColor = marktSituation === 'Krise' ? 'FFDC2626' : '16B364'; // Red or Green
  
  return {
    richText: [
      {
        text: '01.01: ',
        font: { color: { argb: 'FF000000' } } // Black
      },
      {
        text: startOfYearStatus,
        font: { color: { argb: startOfYearColor } }
      },
      {
        text: '\n31.12: ',
        font: { color: { argb: 'FF000000' } } // Black
      },
      {
        text: marktSituation,
        font: { color: { argb: endOfYearColor } }
      }
    ]
  };
};

/**
 * Interface for Excel export data structure
 */
export interface ExcelExportData {
  exportInfo: {
    title: string;
    exportDate: string;
    simulationType: string;
    garchEnhanced: boolean;
  };
  simulationParameters: {
    actualValues: Record<string, any>;
    garchModelParameters?: {
      indexType: string;
      dataPoints: number;
      calibratedParameters?: {
        volatilityPersistence?: string;
        archEffect?: string;
        leverageEffect?: string;
      };
    };
  };
  simulationOptions?: {
    inflationOption: string;
    investmentReturnOption: string;
    tagesgeldOption?: string;
    zinstopfOption?: string;
    crisisDefinition: string;
  };
  simulationResults: Array<{
    alter: number;
    entnahme?: number;
    gesamtentnahme?: number;
    inflationsrate: number;
    gesamtsaldo: number;
    marktlage: {
      startOfYear: 'Hoch' | 'Krise';
      endOfYear: 'Hoch' | 'Krise';
      decisionBasedOn: string;
      timingConsistent: boolean;
    };
    auffuellAktion?: string;
    kapitalIndexDetails?: {
      startOfYear: number;
      endOfYear: number;
      yearlyChange: number;
      yearlyChangePercent: number;
    };
    crisisState?: {
      isInCrisis: boolean;
      crisisStartYear: number;
      precrisisKapitalIndex: number;
      crisisType: string;
    };
    entnahmeAktion?: string;
    rawData?: {
      verbrauchstopfZinssatz?: number;
      zinstopfZinssatz?: number;
      investitionstopfRendite: number;
    };
    verbrauchstopf?: {
      startBalance?: number;
      endBalance?: number;
      transactions?: string[];
    };
    zinstopf?: {
      startBalance?: number;
      endBalance?: number;
      transactions?: string[];
    };
    investitionstopf?: {
      startBalance?: number;
      endBalance?: number;
      interestRate?: number;
      transactions?: string[];
    };
    garchAnalysis?: {
      // LABEL-FIX: γ = Magnituden-Term (Clustering), θ = Leverage (siehe garchModeling.ts)
      clusteringEffect?: string;
      leverageEffect?: string;
    };
  }>;
}

/**
 * Interface for Excel formatting options
 */
export interface ExcelFormattingOptions {
  highlightCrisisYears: boolean;
  highlightNormalYears: boolean;
  redTextForNegativeReturns: boolean;
  includeValidationFormulas: boolean;
  separateTransactionSheet: boolean;
  freezeHeaders: boolean;
  includeCrossReferences: boolean;
}

export class FormattedExcelExportService {
  
  /**
   * Creates a unified Excel workbook with both 3-Topf and 1-Topf simulation data
   */
  static async createUnifiedFormattedWorkbook(
    threeTopfExportData: ExcelExportData,
    oneTopfExportData: ExcelExportData, 
    options: ExcelFormattingOptions
  ): Promise<ExcelJS.Workbook> {
    const workbook = new ExcelJS.Workbook();
    
    // Set workbook properties
    workbook.creator = 'Portfolio 3-Topf Management System';
    workbook.lastModifiedBy = 'Portfolio 3-Topf';
    workbook.created = new Date();
    workbook.modified = new Date();
    
    // Sheet 1: Parameters and explanations (first sheet)
    await this.createFormattedParametersSheet(workbook, threeTopfExportData, oneTopfExportData);
    
    // Sheet 2: 3-Topf simulation data
    await this.createFormatted3TopfMainSheet(workbook, threeTopfExportData, options);
    
    // Sheet 3: 1-Topf simulation data
    await this.createFormatted1TopfMainSheet(workbook, oneTopfExportData, options);
    
    return workbook;
  }
  
  /**
   * Creates the main 3-Topf simulation sheet with full formatting
   */
  private static async createFormatted3TopfMainSheet(
    workbook: ExcelJS.Workbook, 
    exportData: ExcelExportData, 
    options: ExcelFormattingOptions
  ): Promise<ExcelJS.Worksheet> {
    const worksheet = workbook.addWorksheet('3-Topf_Simulation');
    
    // Headers for 3-Topf
    const headers = [
      'Alter', 'Entnahme (€)', 'Inflation (%)', 
      'Verbrauchstopf', 'Rendite V. (%)', 'Zinstopf', 'Rendite Z. (%)', 
      'Investitionstopf', 'Rendite I. (%)', 'Kapitalindex', 
      'Gesamtsaldo (€)', 'Marktlage', 'Info'
    ];
    
    // Add GARCH columns if applicable
    if (exportData.exportInfo.garchEnhanced) {
      headers.push('Volatilität (%)', 'Vol. Regime', 'GARCH Info');
    }
    
    // Add headers to worksheet
    const headerRow = worksheet.addRow(headers);
    
    // Style the header row - White background with dark text
    headerRow.eachCell((cell) => {
      // No background color - white background only
      cell.font = {
        color: { argb: 'FF366092' }, // Dark blue text (professional)
        bold: true,
        size: 11
      };
      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
    
    // Add data rows with formatting
    exportData.simulationResults.forEach((result, index) => {
      // Use capital index data from structured kapitalIndexDetails
      const kapitalIndexEnd = result.kapitalIndexDetails?.endOfYear || (() => {
        // Fallback calculation for backward compatibility
        let calculatedIndex = 100;
        for (let i = 0; i <= index; i++) {
          const yearReturn = (exportData.simulationResults[i].rawData?.investitionstopfRendite || 0) / 100;
          calculatedIndex *= 1 + yearReturn;
        }
        return calculatedIndex;
      })();
      
      const kapitalIndexStart = result.kapitalIndexDetails?.startOfYear || 100;
      
      // Create transaction details like screen table with exact formatting
      const verbrauchstopfDetails = result.verbrauchstopf?.transactions?.join('\n') || 
        `01.01.: ${Math.round(result.verbrauchstopf?.startBalance || 0).toLocaleString('de-DE')} €\n31.12.: ${Math.round(result.verbrauchstopf?.endBalance || 0).toLocaleString('de-DE')} €`;
      
      const zinstopfDetails = result.zinstopf?.transactions?.join('\n') || 
        `01.01.: ${Math.round(result.zinstopf?.startBalance || 0).toLocaleString('de-DE')} €\n31.12.: ${Math.round(result.zinstopf?.endBalance || 0).toLocaleString('de-DE')} €`;
      
      const investitionstopfDetails = result.investitionstopf?.transactions?.join('\n') || 
        `01.01.: ${Math.round(result.investitionstopf?.startBalance || 0).toLocaleString('de-DE')} €\n31.12.: ${Math.round(result.investitionstopf?.endBalance || 0).toLocaleString('de-DE')} €`;
      
      const kapitalindexDetails = `01.01: ${Math.round(kapitalIndexStart).toLocaleString('de-DE')}\n31.12: ${Math.round(kapitalIndexEnd).toLocaleString('de-DE')}`;
      
      // Format market situation with dual timing (01.01 and 31.12) using data from JSON export
      const marktSituationWithDualTiming = formatMarktSituationWithDualTiming(
        result.marktlage.endOfYear,
        result.marktlage.startOfYear
      );

      const row = [
        result.alter,
        result.entnahme || result.gesamtentnahme,
        result.inflationsrate,
        verbrauchstopfDetails,
        result.rawData?.verbrauchstopfZinssatz || 0,
        zinstopfDetails,
        result.rawData?.zinstopfZinssatz || 0,
        investitionstopfDetails,
        result.rawData?.investitionstopfRendite || 0,
        kapitalindexDetails,
        `${Math.round(result.gesamtsaldo).toLocaleString('de-DE')} €`,
        marktSituationWithDualTiming,
        result.auffuellAktion
      ];
      
      // Add GARCH data if applicable - EXACT match to screen table logic
      if (exportData.exportInfo.garchEnhanced) {
        // Volatilität: Rolling 3-year volatility estimation (like estimateCurrentVolatility function)
        let volatility = 16.0; // Default market volatility
        if (index >= 2) {
          const recentReturns = exportData.simulationResults
            .slice(Math.max(0, index - 2), index + 1)
            .map(r => r.rawData?.investitionstopfRendite || 0);
          
          const mean = recentReturns.reduce((sum, r) => sum + r, 0) / recentReturns.length;
          const variance = recentReturns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / recentReturns.length;
          volatility = Math.sqrt(variance);
        }
        
        // Vol. Regime: Based on absolute return (like getVolatilityRegime function)
        const absReturn = Math.abs(result.rawData?.investitionstopfRendite || 0);
        const regime = absReturn > 20 ? 'Hoch' : absReturn > 10 ? 'Mittel' : 'Niedrig';
        
        // GARCH Info: Vol-Cluster and Leverage detection (like screen table)
        const isVolatilityCluster = absReturn > 15;
        const isLeverageEffect = (result.rawData?.investitionstopfRendite || 0) < -10;
        
        const badges = [];
        if (isVolatilityCluster) {badges.push('Vol-Cluster');}
        if (isLeverageEffect) {badges.push('Leverage');}
        const info = badges.length > 0 ? badges.join('\n') : 'Normal';
        
        row.push(volatility, regime, info);
      }
      
      const dataRow = worksheet.addRow(row);
      
      // Apply formatting based on conditions
      const hasNegativeVerbrauchstopfReturn = (result.rawData?.verbrauchstopfZinssatz || 0) < 0;
      const hasNegativeZinstopfReturn = (result.rawData?.zinstopfZinssatz || 0) < 0;
      const hasNegativeInvestitionstopfReturn = (result.rawData?.investitionstopfRendite || 0) < 0;
      
      dataRow.eachCell((cell, colNumber) => {
        // No background colors - keep white background only
        
        // Apply red text for negative returns in specific return columns only when that specific value is negative
        if (options.redTextForNegativeReturns) {
          if (colNumber === 5 && hasNegativeVerbrauchstopfReturn) { // Rendite V. (%)
            cell.font = {
              color: { argb: 'FFDC2626' }, // Cloudscape text-status-error (#DC2626)
              bold: true
            };
          } else if (colNumber === 7 && hasNegativeZinstopfReturn) { // Rendite Z. (%)
            cell.font = {
              color: { argb: 'FFDC2626' }, // Cloudscape text-status-error (#DC2626)
              bold: true
            };
          } else if (colNumber === 9 && hasNegativeInvestitionstopfReturn) { // Rendite I. (%)
            cell.font = {
              color: { argb: 'FFDC2626' }, // Cloudscape text-status-error (#DC2626)
              bold: true
            };
          }
        }
        
        // General cell styling
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        
        // Text wrapping and multi-color formatting for transaction detail columns (4, 6, 8)
        if (colNumber === 4 || colNumber === 6 || colNumber === 8) {
          cell.alignment = {
            horizontal: 'right',
            vertical: 'top',
            wrapText: true
          };
          
          // Apply multi-color transaction formatting (like screen table)
          const cellText = cell.value?.toString() || '';
          const lines = cellText.split('\n');
          
          // Create rich text with proper colors for each line
          const richTextParts: any[] = [];
          
          lines.forEach((line, lineIndex) => {
            if (lineIndex > 0) {
              richTextParts.push({ text: '\n' }); // Add line break
            }
            
            // Determine color based on transaction type (exact screen table logic)
            let color = '#000000'; // Default black (text-body-secondary)
            
            if (line.includes('01.01.:') || line.includes('31.12.:')) {
              // Start and end balance - Black (text-body-secondary)
              color = '#000000';
            } else if (line.includes('- (Entnahme)') || line.includes('- (in ') || line.includes('- (Überschuss an ')) {
              // Withdrawals, outflows, and overflow transfers OUT - Red (text-status-error)
              color = '#DC2626';
            } else if (line.includes('+ (aus ') || line.includes('+ (Überschuss aus ')) {
              // Inflows and overflow transfers IN - Green (text-status-success)
              color = '#059669';
            } else if (line.includes('(Rendite)') || line.includes('(Zinsen)')) {
              // Returns - Red for negative, Green for positive
              color = line.includes('+') ? '#059669' : '#DC2626';
            }
            
            richTextParts.push({
              text: line,
              font: { color: { argb: 'FF' + color.substring(1) } }
            });
          });
          
          // Apply rich text if we have multiple colors, otherwise use simple formatting
          if (richTextParts.some(part => part.font?.color?.argb !== 'FF000000')) {
            cell.value = {
              richText: richTextParts
            };
          }
        } else if (colNumber === 10) {
          // Kapitalindex column - Center alignment with text wrapping
          cell.alignment = {
            horizontal: 'center',
            vertical: 'middle',
            wrapText: true
          };
        } else if (colNumber === 12) {
          // Markt column - Handle both simple text and rich text crisis formatting
          cell.alignment = {
            horizontal: 'center',
            vertical: 'middle',
            wrapText: true
          };
          
          // Check if it's already rich text (from formatMarktSituationWithCrisis)
          if (typeof cell.value === 'object' && cell.value !== null && 'richText' in cell.value) {
            // Rich text is already set by formatMarktSituationWithCrisis function
            // No additional formatting needed
          } else {
            // Simple text - apply color based on market situation
            const marketValue = cell.value?.toString() || '';
            if (marketValue === 'Krise') {
              cell.font = {
                color: { argb: 'FFDC2626' }, // Red text for crisis (Badge red)
                bold: true
              };
            } else {
              cell.font = {
                color: { argb: 'FF059669' }, // Green text for normal (Badge green)
                bold: true
              };
            }
          }
        } else if (colNumber === 13) {
          // Info column (13) - Left alignment and text wrapping for multiline content
          cell.alignment = {
            horizontal: 'left',
            vertical: 'top',
            wrapText: true
          };
        } else {
          // Center alignment for other columns
          cell.alignment = {
            horizontal: 'center',
            vertical: 'middle'
          };
        }
        
        // Special formatting for GARCH Info column (enable text wrapping for multi-line content)
        if (exportData.exportInfo.garchEnhanced && colNumber === headers.length) { // Last column is GARCH Info
          cell.alignment = {
            horizontal: 'center',
            vertical: 'middle',
            wrapText: true
          };
        }
        
        // Number formatting for specific columns
        if (colNumber === 2) {
          // Entnahme (€) column - Currency without decimals
          cell.numFmt = '#,##0 "€"';
        } else if (colNumber === 3) {
          // Inflation (%) column - Already in % format, just add % symbol
          cell.numFmt = '0.0"%"';
        } else if (colNumber === 5 || colNumber === 7 || colNumber === 9) {
          // Return percentage columns (Rendite V. %, Rendite Z. %, Rendite I. %) - Already in % format
          cell.numFmt = '0.0"%"';
        } else if (exportData.exportInfo.garchEnhanced && colNumber === headers.length - 2) {
          // Volatilität (%) column - First GARCH column
          cell.numFmt = '0.0"%"';
        }
      });
    });
    
    // Set column widths - EXACT match to screen table
    const columnWidths = [
      { width: 8 },   // Alter (60px -> 8 chars)
      { width: 15 },  // Entnahme (120px -> 15 chars)
      { width: 10 },  // Inflation (80px -> 10 chars)
      { width: 25 },  // Verbrauchstopf (200px -> 25 chars)
      { width: 12 },  // Rendite V. (80px -> 12 chars)
      { width: 22 },  // Zinstopf (180px -> 22 chars)
      { width: 12 },  // Rendite Z. (80px -> 12 chars)
      { width: 25 },  // Investitionstopf (200px -> 25 chars)
      { width: 12 },  // Rendite I. (80px -> 12 chars)
      { width: 15 },  // Kapitalindex (120px -> 15 chars)
      { width: 17 },  // Gesamtsaldo (140px -> 17 chars)
      { width: 10 },  // Marktlage (80px -> 10 chars)
      { width: 70 }   // Info (550px -> 70 chars)
    ];
    
    // Add GARCH column widths if applicable
    if (exportData.exportInfo.garchEnhanced) {
      columnWidths.push(
        { width: 12 }, // Volatilität (90px -> 12 chars)
        { width: 12 }, // Vol. Regime (90px -> 12 chars)
        { width: 15 }  // GARCH Info (120px -> 15 chars)
      );
    }
    
    worksheet.columns = columnWidths;
    
    // Freeze the header row
    if (options.freezeHeaders) {
      worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    }
    
    // Auto-filter removed per user request
    
    return worksheet;
  }
  
  /**
   * Creates the main 1-Topf simulation sheet with full formatting
   */
  private static async createFormatted1TopfMainSheet(
    workbook: ExcelJS.Workbook, 
    exportData: ExcelExportData, 
    options: ExcelFormattingOptions
  ): Promise<ExcelJS.Worksheet> {
    const worksheet = workbook.addWorksheet('1-Topf_Simulation');
    
    // Headers for 1-Topf - Match screen table structure
    const headers = [
      'Alter', 'Entnahme (€)', 'Inflation (%)', 
      'Investitionstopf', 'Rendite I. (%)',
      'Kapitalindex', 'Marktlage', 'Info'
    ];
    
    // Add GARCH columns if applicable
    if (exportData.exportInfo.garchEnhanced) {
      headers.push('Volatilität (%)', 'Vol. Regime', 'GARCH Info');
    }
    
    // Add headers with styling
    const headerRow = worksheet.addRow(headers);
    
    // Style the header row - White background with dark text (same as 3-Topf)
    headerRow.eachCell((cell) => {
      // No background color - white background only
      cell.font = {
        color: { argb: 'FF366092' }, // Dark blue text (professional)
        bold: true,
        size: 11
      };
      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
    
    // Add data rows with formatting
    exportData.simulationResults.forEach((result, index) => {
      // Use capital index data from structured kapitalIndexDetails for 1-Topf
      const kapitalIndexEnd = result.kapitalIndexDetails?.endOfYear || (() => {
        // Fallback calculation for backward compatibility
        let calculatedIndex = 100;
        for (let i = 0; i <= index; i++) {
          const yearReturn = (exportData.simulationResults[i].investitionstopf?.interestRate || 0) / 100;
          calculatedIndex *= 1 + yearReturn;
        }
        return calculatedIndex;
      })();
      
      const kapitalIndexStart = result.kapitalIndexDetails?.startOfYear || 100;
      
      // Create transaction details like screen table for 1-Topf
      const investitionstopfDetails = result.investitionstopf?.transactions?.join('\n') || 
        `01.01.: ${Math.round(result.investitionstopf?.startBalance || 0).toLocaleString('de-DE')} €\n31.12.: ${Math.round(result.investitionstopf?.endBalance || 0).toLocaleString('de-DE')} €`;
      
      const kapitalindexDetails = `01.01: ${Math.round(kapitalIndexStart).toLocaleString('de-DE')}\n31.12: ${Math.round(kapitalIndexEnd).toLocaleString('de-DE')}`;
      
      // Generate Info description - prefer the actual entnahmeAktion from the simulation
      // (e.g. "Partielle Entnahme ... (Portfolio erschöpft)") over the reconstructed default
      const entnahmeAktion = result.entnahmeAktion ||
        `• Investitionstopf-Entnahme: ${Math.round(result.entnahme || result.gesamtentnahme || 0).toLocaleString('de-DE')} €`;
      
      // Format market situation with dual timing (01.01 and 31.12) for 1-Topf using data from JSON export
      const marktSituationWithDualTiming1Topf = formatMarktSituationWithDualTiming(
        result.marktlage.endOfYear,
        result.marktlage.startOfYear
      );
      
      const row = [
        result.alter,
        result.entnahme || result.gesamtentnahme,
        result.inflationsrate,
        investitionstopfDetails,
        result.investitionstopf?.interestRate || 0,
        kapitalindexDetails,
        marktSituationWithDualTiming1Topf,
        entnahmeAktion
      ];
      
      // Add GARCH data if applicable - EXACT match to screen table logic for 1-Topf
      if (exportData.exportInfo.garchEnhanced) {
        // Volatilität: Rolling 3-year volatility estimation (like 1-Topf screen table)
        let volatility = 16.0; // Default market volatility
        if (index >= 2) {
          const recentReturns = exportData.simulationResults
            .slice(Math.max(0, index - 2), index + 1)
            .map(r => r.investitionstopf?.interestRate || 0);
          
          const mean = recentReturns.reduce((sum, r) => sum + r, 0) / recentReturns.length;
          const variance = recentReturns.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / recentReturns.length;
          volatility = Math.sqrt(variance);
        }
        
        // Vol. Regime: Based on absolute return (like getVolatilityRegime function)
        const absReturn = Math.abs(result.investitionstopf?.interestRate || 0);
        const regime = absReturn > 20 ? 'Hoch' : absReturn > 10 ? 'Mittel' : 'Niedrig';
        
        // GARCH Info: Vol-Cluster and Leverage detection (like screen table)
        const isVolatilityCluster = absReturn > 15;
        const isLeverageEffect = (result.investitionstopf?.interestRate || 0) < -10;
        
        const badges = [];
        if (isVolatilityCluster) {badges.push('Vol-Cluster');}
        if (isLeverageEffect) {badges.push('Leverage');}
        const info = badges.length > 0 ? badges.join('\n') : 'Normal';
        
        row.push(volatility, regime, info);
      }
      
      const dataRow = worksheet.addRow(row);
      
      // Apply formatting based on conditions
      const hasNegativeReturn = (result.investitionstopf?.interestRate || 0) < 0;
      
      dataRow.eachCell((cell, colNumber) => {
        // No background colors - keep white background only
        
        // Apply red text for negative returns in return column (5) - EXACT Cloudscape text-status-error
        if (options.redTextForNegativeReturns && hasNegativeReturn && colNumber === 5) {
          cell.font = {
            color: { argb: 'FFDC2626' }, // Cloudscape text-status-error (#DC2626)
            bold: true
          };
        }
        
        // General cell styling
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        
        // Text wrapping and multi-color formatting for transaction detail columns (4 only)
        if (colNumber === 4) {
          cell.alignment = {
            horizontal: 'right',
            vertical: 'top',
            wrapText: true
          };
          
          // Apply multi-color transaction formatting (like 1-Topf screen table)
          const cellText = cell.value?.toString() || '';
          const lines = cellText.split('\n');
          
          // Create rich text with proper colors for each line
          const richTextParts: any[] = [];
          
          lines.forEach((line, lineIndex) => {
            if (lineIndex > 0) {
              richTextParts.push({ text: '\n' }); // Add line break
            }
            
            // Determine color based on transaction type (exact 1-Topf screen table logic)
            let color = '#000000'; // Default black (inherit)
            
            if (line.includes('01.01.:') || line.includes('31.12.:')) {
              // Start and end balance - Black (inherit)
              color = '#000000';
            } else if (line.includes('- (Entnahme)')) {
              // Withdrawals - Red (text-status-error)
              color = '#DC2626';
            } else if (line.includes('(Rendite)')) {
              // Returns - Red for negative, Green for positive
              color = line.includes('+') ? '#059669' : '#DC2626';
            }
            
            richTextParts.push({
              text: line,
              font: { color: { argb: 'FF' + color.substring(1) } }
            });
          });
          
          // Apply rich text if we have multiple colors, otherwise use simple formatting
          if (richTextParts.some(part => part.font?.color?.argb !== 'FF000000')) {
            cell.value = {
              richText: richTextParts
            };
          }
        } else if (colNumber === 6) {
          // Kapitalindex column - Center alignment with text wrapping
          cell.alignment = {
            horizontal: 'center',
            vertical: 'middle',
            wrapText: true
          };
        } else if (colNumber === 7) {
          // Markt column - Handle both simple text and rich text crisis formatting
          cell.alignment = {
            horizontal: 'center',
            vertical: 'middle',
            wrapText: true
          };
          
          // Check if it's already rich text (from formatMarktSituationWithCrisis)
          if (typeof cell.value === 'object' && cell.value !== null && 'richText' in cell.value) {
            // Rich text is already set by formatMarktSituationWithCrisis function
            // No additional formatting needed
          } else {
            // Simple text - apply color based on market situation
            const marketValue = cell.value?.toString() || '';
            if (marketValue === 'Krise') {
              cell.font = {
                color: { argb: 'FFDC2626' }, // Red text for crisis (Badge red)
                bold: true
              };
            } else {
              cell.font = {
                color: { argb: 'FF059669' }, // Green text for normal (Badge green)
                bold: true
              };
            }
          }
        } else {
          // Center alignment for other columns
          cell.alignment = {
            horizontal: 'center',
            vertical: 'middle'
          };
        }
        
        // Special formatting for GARCH Info column (enable text wrapping for multi-line content)
        if (exportData.exportInfo.garchEnhanced && colNumber === headers.length) { // Last column is GARCH Info
          cell.alignment = {
            horizontal: 'center',
            vertical: 'middle',
            wrapText: true
          };
        }
        
        // Number formatting
        if (colNumber === 2) {
          // Entnahme (€) column - Currency without decimals
          cell.numFmt = '#,##0 "€"';
        } else if (colNumber === 3 || colNumber === 5) {
          // Percentage columns (Inflation %, Rendite I. %) - Already in % format, just add % symbol
          cell.numFmt = '0.0"%"';
        } else if (exportData.exportInfo.garchEnhanced && colNumber === headers.length - 2) {
          // Volatilität (%) column - First GARCH column
          cell.numFmt = '0.0"%"';
        }
      });
    });
    
    // Set column widths - EXACT match to screen table
    const columnWidths = [
      { width: 8 },   // Alter (60px -> 8 chars)
      { width: 15 },  // Entnahme (120px -> 15 chars)
      { width: 10 },  // Inflation (80px -> 10 chars)
      { width: 25 },  // Investitionstopf (200px -> 25 chars)
      { width: 10 },  // Rendite I. (80px -> 10 chars)
      { width: 15 },  // Kapitalindex (120px -> 15 chars)
      { width: 14 },  // Marktlage (dual timing "01.01/31.12" -> 14 chars)
      { width: 40 }   // Info (Entnahme-Aktion text -> 40 chars)
    ];
    
    // Add GARCH column widths if applicable
    if (exportData.exportInfo.garchEnhanced) {
      columnWidths.push(
        { width: 12 }, // Volatilität (90px -> 12 chars)
        { width: 12 }, // Vol. Regime (90px -> 12 chars)
        { width: 15 }  // GARCH Info (120px -> 15 chars)
      );
    }
    
    worksheet.columns = columnWidths;
    
    // Freeze the header row
    if (options.freezeHeaders) {
      worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    }
    
    // Auto-filter removed per user request
    
    return worksheet;
  }
  
  /**
   * Creates formatted parameters sheet with detailed explanations
   */
  private static async createFormattedParametersSheet(
    workbook: ExcelJS.Workbook, 
    threeTopfExportData: ExcelExportData,
    _oneTopfExportData: ExcelExportData
  ): Promise<ExcelJS.Worksheet> {
    const worksheet = workbook.addWorksheet('Parameter_Erklaerung');
    
    // Add main title
    const titleRow = worksheet.addRow(['PORTFOLIO SIMULATION PARAMETERS & EXPLANATIONS']);
    titleRow.getCell(1).font = { bold: true, size: 16, color: { argb: 'FF366092' } };
    worksheet.addRow([]);
    
    // Export information
    worksheet.addRow(['Export Information']);
    const exportInfoHeader = worksheet.lastRow;
    if (exportInfoHeader) {
      exportInfoHeader.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF366092' } };
    }
    worksheet.addRow(['Export Date', threeTopfExportData.exportInfo.exportDate]);
    worksheet.addRow(['GARCH Enhanced', threeTopfExportData.exportInfo.garchEnhanced ? 'Yes' : 'No']);
    worksheet.addRow([]);
    
    // Selected simulation options
    if (threeTopfExportData.simulationOptions) {
      worksheet.addRow(['Selected Simulation Options']);
      const optionsHeader = worksheet.lastRow;
      if (optionsHeader) {
        optionsHeader.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF366092' } };
      }
      
      const params = threeTopfExportData.simulationParameters.actualValues;
      const options = threeTopfExportData.simulationOptions;
      
      worksheet.addRow(['Inflation Option', getInflationOptionLabel(options.inflationOption, params)]);
      worksheet.addRow(['Investment Return Option', getInvestmentReturnOptionLabel(options.investmentReturnOption, params)]);
      if (options.tagesgeldOption) {
        worksheet.addRow(['Tagesgeld Option', getTagesgeldOptionLabel(options.tagesgeldOption, params)]);
      }
      if (options.zinstopfOption) {
        worksheet.addRow(['Zinstopf Option', getZinstopfOptionLabel(options.zinstopfOption, params)]);
      }
      worksheet.addRow(['Crisis Definition', getCrisisDefinitionLabel(options.crisisDefinition)]);
      worksheet.addRow([]);
    }
    
    // Strategy comparison
    worksheet.addRow(['Strategy Comparison']);
    const strategyHeader = worksheet.lastRow;
    if (strategyHeader) {
      strategyHeader.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF366092' } };
    }
    worksheet.addRow(['3-Topf Strategy', 'Aufbau von drei separaten Töpfen (Verbrauchs-, Zins-, Investitionstopf)']);
    worksheet.addRow(['1-Topf Strategy', 'Einfache Strategie mit nur einem Investitionstopf']);
    worksheet.addRow([]);
    
    // Core simulation parameters (filtered)
    worksheet.addRow(['Core Simulation Parameters']);
    const coreHeader = worksheet.lastRow;
    if (coreHeader) {
      coreHeader.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF366092' } };
    }
    const params = threeTopfExportData.simulationParameters.actualValues;
    
    // Filter out the specific parameters that should not be shown
    const excludedParams = ['zinssatzVerbrauchstopf', 'zinssatzZinstopf', 'renditeInvestitionstopf', 'inflationsrate'];
    
    Object.entries(params).forEach(([key, value]) => {
      if (!excludedParams.includes(key)) {
        worksheet.addRow([key, value]);
      }
    });
    worksheet.addRow([]);
    
    // GARCH model explanation (if used)
    if (threeTopfExportData.exportInfo.garchEnhanced && threeTopfExportData.simulationParameters?.garchModelParameters) {
      worksheet.addRow(['GARCH Volatility Model']);
      const garchHeader = worksheet.lastRow;
      if (garchHeader) {
        garchHeader.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF366092' } };
      }
      worksheet.addRow(['Model Type', 'EGARCH(1,1)']);
      worksheet.addRow(['Purpose', 'Realistic volatility modeling']);
      worksheet.addRow(['Data Source', threeTopfExportData.simulationParameters.garchModelParameters.indexType]);
      worksheet.addRow(['Data Points', threeTopfExportData.simulationParameters.garchModelParameters.dataPoints]);
      worksheet.addRow([]);

      // GARCH parameters with explanations
      worksheet.addRow(['GARCH Model Parameters']);
      const garchParamsHeader = worksheet.lastRow;
      if (garchParamsHeader) {
        garchParamsHeader.getCell(1).font = { bold: true, size: 11, color: { argb: 'FF366092' } };
      }
      if (threeTopfExportData.simulationParameters.garchModelParameters.calibratedParameters) {
        const garchParams = threeTopfExportData.simulationParameters.garchModelParameters.calibratedParameters;
        worksheet.addRow(['Volatility Persistence', garchParams.volatilityPersistence || 'N/A']);
        worksheet.addRow(['ARCH Effect', garchParams.archEffect || 'N/A']);
        worksheet.addRow(['Leverage Effect', garchParams.leverageEffect || 'N/A']);
      }
      worksheet.addRow([]);
    }
    
    // Market situation explanations
    worksheet.addRow(['Market Situation Definitions']);
    const marketHeader = worksheet.lastRow;
    if (marketHeader) {
      marketHeader.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF366092' } };
    }
    // Describe the actually selected crisis definition instead of a fixed default
    const selectedCrisisDefinition =
      threeTopfExportData.simulationOptions?.crisisDefinition || 'negative-return';
    if (selectedCrisisDefinition === 'kapitalindex-drawdown') {
      worksheet.addRow(['Crisis Definition', getCrisisDefinitionLabel(selectedCrisisDefinition)]);
      worksheet.addRow(['Krise (Crisis)', 'Capital index below pre-crisis level (crisis ends only after full recovery)']);
      worksheet.addRow(['Normal', 'Capital index at or above pre-crisis level']);
    } else {
      worksheet.addRow(['Crisis Definition', getCrisisDefinitionLabel(selectedCrisisDefinition)]);
      worksheet.addRow(['Krise (Crisis)', 'Negative investment returns']);
      worksheet.addRow(['Normal', 'Positive investment returns']);
    }
    worksheet.addRow([]);
    
    // Color coding explanation
    worksheet.addRow(['Color Coding in Simulation Tables']);
    const colorHeader = worksheet.lastRow;
    if (colorHeader) {
      colorHeader.getCell(1).font = { bold: true, size: 12, color: { argb: 'FF366092' } };
    }
    worksheet.addRow(['Red Text', 'Negative returns, withdrawals, crisis years']);
    worksheet.addRow(['Green Text', 'Positive returns, inflows, normal years']);
    worksheet.addRow(['Black Text', 'Start/end balances, neutral data']);
    
    // Set column widths for 2-column layout
    worksheet.columns = [
      { width: 35 }, // Parameter name
      { width: 45 }  // Value
    ];
    
    return worksheet;
  }
  
  /**
   * Downloads the Excel workbook with unified filename
   */
  static async downloadFormattedExcelFile(workbook: ExcelJS.Workbook, filename?: string): Promise<void> {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `portfolio-simulation-${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}